/**
 * Type Exports
 *
 * Pure barrel file — re-exports from domain modules.
 * No inline type definitions here.
 */

// =============================================================================
// Abilities
// =============================================================================

export type {
  AbilityScope,
  AbilityManifest,
  Ability,
  AbilityFile,
  UserAbility,
} from './ability.js';
export { ESSENTIALS_SLUG } from './ability.js';

// =============================================================================
// Abilities — Marketplace + Recommendation surface (Phase 3)
// =============================================================================

export type {
  HookIntent,
  MarketplaceHookEvent,
  HookSummary,
  AbilityAuthor,
  MarketplaceAbility,
  MarketplaceAbilityDetail,
  InstallPreview,
  SearchAbilitiesResult,
  AbilityState,
  CatalogAbility,
  CatalogAbilityDetail,
} from './abilities-marketplace.js';

// =============================================================================
// Abilities — Skills + Agents Catalog (Task 0.2)
// =============================================================================

export type { SkillCatalogState, CatalogSkill } from './skills-catalog.js';
export type { AgentCatalogState, CatalogAgent } from './agents-catalog.js';
export type { AbilitySkillRow, AbilityAgentRow } from './ability-joins.js';

// =============================================================================
// Connected accounts (Composio multi-account, design §5)
// =============================================================================

export type {
  ConnectedAccountStatus,
  ConnectedAccount,
  ConnectedAccountWire,
  AccountSummary,
  ConnectedAccountsRoster,
} from './connected-account.js';
export {
  connectedAccountStatusFrom,
  accountNeedsReconnect,
  toConnectedAccountWire,
  accountSummaryFrom,
  accountSummaryFromWire,
  serializeAccountsRoster,
  parseAccountsRosterJson,
} from './connected-account.js';
export type { ComposioToolHintWire, ComposioToolHintsResponse } from './composio-tool-hints.js';

// =============================================================================
// Session (legacy - kept for backwards compatibility)
// =============================================================================

export type {
  PermissionMode,
  SessionStatus,
  PendingInputType,
  PendingInput,
  Session,
  SerializedSession,
} from './session.js';
export { serializeSession } from './session.js';

// =============================================================================
// Task (Single ID: taskId === sdkSessionId)
// =============================================================================

export type {
  TaskStatus,
  TaskLifecycleState,
  PendingReason,
  PendingAuthDetails,
  PendingQuestionDetails,
  PendingApprovalDetails,
  PendingPermissionDetails,
  PendingErrorDetails,
  PendingCustomDetails,
  PendingCreditDetails,
  PendingHandsDetails,
  PendingPlanLimitDetails,
  PendingDetails,
  Task,
  SerializedTask,
  DraftTask,
  TaskCreatedPayload,
  TaskResumePayload,
  TaskResumedPayload,
  TaskListPayload,
  TaskUpdatePayload,
  TaskStreamPayload,
  TaskResumeFailedPayload,
  TaskTitleEditPayload,
  TaskStatsPayload,
  TaskOutputType,
  TaskOutput,
  TaskTodoGroup,
} from './task.js';
export { TERMINAL_TASK_LIFECYCLE_STATES, TASK_IDENTITY_FIELD } from './task.js';

// =============================================================================
// Presence (Phase 1 connection-state redesign — 2026-04-23)
// =============================================================================

export type {
  Presence,
  PresencePhase,
  UpgradeProgress,
  PresenceError,
  PresenceWall,
  SandboxRepair,
  SandboxRepairAction,
} from './presence.js';
export { PRESENCE_PHASES } from './presence.js';

// =============================================================================
// Unified State (Server as Single Source of Truth)
// =============================================================================

export type {
  // Display Status (Phase 13 — server sends, frontend renders)
  DisplayStatus,

  // Unified Lifecycle (Phase 12)
  UnifiedLifecycle,

  // Server-Side Message Storage
  StoredMessage,
  SerializedStoredMessage,

  // Event Payloads for State Sync
  MessageSyncPayload,

  // Connection Lifecycle State (Unified Connection Service)
  ConnectionLifecycleState,

  // Sandbox Lifecycle (Persists Across Connections)
  SandboxLifecycle,

  // Integration Auth Payloads
  ConnectionRequiredPayload,
} from './state.js';

export {
  serializeStoredMessage,
  toUnifiedLifecycle,
  sandboxToUnifiedLifecycle,
  isLifecycleReady,
  isLifecycleLoading,
} from './state.js';

// =============================================================================
// Credits
// =============================================================================

export type {
  CreditLedgerRow,
  CreditTransactionType,
  BackgroundAgentSource,
  CreditMetadata,
  CreditBalanceRow,
  PricingConfigRow,
  UserCreditSettingsRow,
  CreditUpdatePayload,
  CreditWarningPayload,
  CreditExhaustedPayload,
  CreditPermissionResponsePayload,
  CreditToolCheckPayload,
  CreditToolVerdictPayload,
  CreditApprovalNeededPayload,
  CreditSyncPayload,
  CreditSummary,
  CreditHistoryEntry,
  TaskCreditSummary,
  TaskCreditBreakdown,
} from './credits.js';

// =============================================================================
// Images
// =============================================================================

export type {
  ImageMediaType,
  ImageAttachment,
  FileAttachment,
  SDKImageSource,
} from './image.js';
export { toSDKImageSource } from './image.js';

// =============================================================================
// Messages & Content Blocks
// =============================================================================

export type {
  MessageRole,
  ToolStatus,
  TodoStatus,
  ComposioToolCategory,
  FormattedResultItem,
  FormattedResult,
  ToolDisplay,
  ToolResultDisplay,
  TextBlock,
  ImageBlock,
  ThinkingBlock,
  ToolUseBlock,
  ToolResultBlock,
  QuestionOption,
  Question,
  QuestionBlock,
  Todo,
  TodoBlock,
  ErrorBlock,
  FileAttachmentBlock,
  MediaBlock,
  CreditApprovalBlock,
  RoutineConfirmationBlock,
  ItemConfirmationBlock,
  TurnFooterBlock,
  CheckpointBlock,
  CompactionBlock,
  MemoryRecallBlock,
  NoticeBlock,
  ContentBlock,
  ContentBlockType,
  Message,
  MessageIdentity,
} from './messages.js';
export {
  isTextBlock,
  isImageBlock,
  isThinkingBlock,
  isToolUseBlock,
  isToolResultBlock,
  isQuestionBlock,
  isTodoBlock,
  isErrorBlock,
  isFileAttachmentBlock,
  isCreditApprovalBlock,
  isRoutineConfirmationBlock,
  isItemConfirmationBlock,
  isTurnFooterBlock,
  isCheckpointBlock,
  isCompactionBlock,
  isMemoryRecallBlock,
  isNoticeBlock,
  getMessageCanonicalId,
  matchesAnyMessageId,
} from './messages.js';

// =============================================================================
// Event Payloads
// =============================================================================

export type {
  ToolIconHint,
  ToolSummary,
  SessionCreatePayload,
  SessionCreatedPayload,
  SessionListPayload,
  SessionResumePayload,
  SessionUpdatePayload,
  AgentRegisterPayload,
  SessionConfigPayload,
  UserMessagePayload,
  UserMessageToAgentPayload,
  AgentStartPayload,
  AgentEndPayload,
  AgentErrorPayload,
  AgentInterruptPayload,
  SystemInitPayload,
  AgentResultPayload,
  AgentThinkingPayload,
  AgentThinkingDeltaPayload,
  AgentThinkingStartPayload,
  AgentTextPayload,
  AgentTextDeltaPayload,
  AgentToolUsePayload,
  AgentToolResultPayload,
  AgentTodoPayload,
  AgentNotebookPayload,
  AgentUsagePayload,
  ContextUsageSnapshot,
  AgentContextUsagePayload,
  MemoryRecallEntry,
  AgentMemoryRecallPayload,
  MessageStagedPayload,
  MessagePickedUpPayload,
  CancelStagedPayload,
  StagedEnvelope,
  StagingSyncRequestPayload,
  StagingSyncPayload,
  TaskOutputRegisteredPayload,
  SubagentSpawnedPayload,
  SubagentMessagePayload,
  // The CLOSED set of report reasons (D11). Re-exported so a consumer that maps every
  // reason to words can key its map by the type and get a compile error on a seventh,
  // rather than a row that silently reads "finished" (M1).
  SubagentReportStopReason,
  PromptSuggestionPayload,
  NamiNotifyPayload,
  ComposerScreenPayload,
  DepositAckPayload,
  TaskPreviewReadyPayload,
  ComputerShowPayload,
  ComputerClosePayload,
  TaskPreviewClosedPayload,
  // Vendor-engine sign-in — the server persists the status the box reports and
  // sends the logout command, so both payload types cross the package boundary.
  EngineAuthStatusPayload,
  EngineAuthLogoutPayload,
  TaskNoticePayload,
  PermissionDeniedPayload,
  EngineStandingPayload,
  EngineStandingRefreshPayload,
  // Settings → Connect in place (engines T2a): server, box and web all speak these.
  EngineConnectPayload,
  EngineConnectCancelPayload,
  EngineConnectProgressPayload,
  EngineConnectPhase,
  EngineConnectFailure,
  EventPayloadMap,
} from './events.js';

// The lead's handle on the peer wire — a VALUE, because the runtime, the delivery
// prefix and the web's row label all compare against the same word (§4.5, R25).
export { LEAD_PEER_HANDLE } from './events.js';
// WHO sent a message (spec 2026-09-12). One field, set at birth, read by one function —
// replacing four surfaces each inferring authorship from the ABSENCE of a stamp.
export type { MessageSender } from './message-sender.js';
export { senderFromPeer, withSender, readSender, senderLabel } from './message-sender.js';

// =============================================================================
// Turn Outcome (termination taxonomy)
// =============================================================================

export type {
  TurnOutcome,
  TurnOutcomeKind,
  RateLimitInfoSummary,
  DeferredToolUseSummary,
  ModelUsageEntry,
  ModelUsageSummary,
  TerminationRaw,
} from './turn-outcome.js';
export { TURN_OUTCOME_KINDS, isErrorOutcome, cacheReadRatioFromModelUsage } from './turn-outcome.js';

// Agent trace store (observability Slice B)
export type {
  TraceId,
  Arm,
  Surface,
  TerminalStatus,
  ErrorSource,
  ErrorSeverity,
  AgentTurnRecord,
  ToolCallRecord,
  InnerToolOutcome,
  ErrorEventRecord,
} from './agent-trace.js';
export { mintTraceId } from './agent-trace.js';
// Our own traces (OTEL_TRACES_V1): the W3C traceparent both halves read and write.
export { formatTraceparent, parseTraceparent, type TraceParent } from './trace-context.js';

// =============================================================================
// Terminal Reason (user-facing turn termination view)
// =============================================================================

export * from './terminal-reason.js';

// =============================================================================
// Output Format (K6 P6 — structured-output declaration)
// =============================================================================

export * from './output-format.js';
// validateOutputValue / validateOutputSchema — pure subset validator
// (re-exported from @vestra/agent-sdk) so the server can defensively re-validate
// a create-routine run's structured_output before calling createRoutine().
export * from './validate-output.js';

// =============================================================================
// Agents
// =============================================================================

export type {
  AgentSlug,
  AgentDefinition,
  AgentSummary,
  PublicAgentSummary,
  AgentMode,
} from './agent.js';
export { AGENT_PORT, AGENT_MODE_MODELS, OPENAI_MODE_MODELS, MODE_MODELS_BY_RUNTIME, DEFAULT_AGENT_MODE, resolveAgentModel, providerToHarnessId, PROVIDERS_BY_HARNESS, isModelAllowedForHarness, isModelAllowedOnEngine, isModelPinnable } from './agent.js';

// =============================================================================
// Skills
// =============================================================================

export type {
  SkillDefinition,
  SkillSummary,
  PublicSkillSummary,
  PublicSkillDetail,
  SkillFile,
  SkillSyncResult,
  SkillChangeDetection,
  SkillSyncStatus,
  SkillImportResult,
  SkillUpdate,
} from './skill.js';

// =============================================================================
// Sandbox
// =============================================================================

export type {
  SandboxStatus,
  UserSandbox,
  SerializedUserSandbox,
} from './sandbox.js';
export { serializeUserSandbox } from './sandbox.js';

// =============================================================================
// Support Chat
// =============================================================================

export type {
  SupportConversationStatus,
  SupportPriority,
  SupportSenderType,
  SupportResolvedBy,
  SupportConversation,
  SupportMessage,
  SupportAttachment,
  SupportArticle,
  SupportCannedResponse,
  SupportConversationOpenPayload,
  SupportMessagePayload,
  SupportNotePayload,
  SupportAssignPayload,
  SupportResolvePayload,
  SupportPriorityPayload,
  SupportTypingPayload,
  SupportMessageReceivedPayload,
  SupportConversationUpdatePayload,
  SupportMessagesReadPayload,
  SupportRecentTask,
  SupportRatePayload,
  SupportLinkTaskPayload,
  SupportAttachmentSignPayload,
  SupportAttachmentSignResponse,
  SupportUserContext,
  SupportAnalytics,
} from './support.js';

// =============================================================================
// User Files (Persistent File Library)
// =============================================================================

export type {
  FileSource,
  FileCategory,
  SyncStatus,
  FileVisibility,
  UserFolder,
  UserFile,
  UserFilesListing,
  UserFilesSyncPayload,
  UserCreateFilePayload,
  UserFileUpdatePayload,
  UserFileVisibilityPayload,
  UserFilesBatchDeletePayload,
  UserFilesBatchMovePayload,
  UserFileReadyPayload,
} from './files.js';

// Value export: both the web create/rename guard and the server's volume-path
// placement rail must agree on the reserved root-folder names.
export { isReservedFolderName, isRefusedLibraryName, libraryNameRefusal } from './files.js';

// Sharing (Train B): the shapes the share routes and the web agree on, and the
// email normalizer the lookup and the dialog both use.
export * from './sharing.js';

// =============================================================================
// Memory System
// =============================================================================

export type {
  MemoryCategory,
  UserMemoryRow,
  MemoryEntry,
  MemoryListResponse,
  MemoryUpdatePayload,
  ExtractedMemory,
  ExtractionResult,
} from './memory.js';

// =============================================================================
// Routines (Automated Task Execution)
// =============================================================================

export type {
  RoutineTriggerType,
  RoutineRunStatus,
  RoutineKind,
  RoutineStatus,
  RoutineSystemKind,
  ScheduleTriggerConfig,
  WebhookTriggerConfig,
  AppEventTriggerConfig,
  AppEventTriggerStatus,
  RoutineTriggerConfig,
  RoutineArmStatus,
  RoutineArmReason,
  RoutineArmState,
  RoutineHoldKind,
  RoutineHold,
  RoutineRunSpec,
  RoutineContract,
  RoutineDefinition,
  RoutineSummary,
  RoutineRun,
} from './routine.js';

// P4 producer-path validation allow-lists (runtime consts).
export { ROUTINE_DELIVERY_MODES, POLITENESS_POLICIES, ROUTINE_HOLD_KINDS, ROUTINE_SYSTEM_KINDS, isRoutineSystemKind } from './routine.js';

// RoutineSpecSchema — K6 P6 flagship structured-output schema (runtime const)
export { RoutineSpecSchema } from './routine-spec.js';

// Automations v2 — platform fire framing (the single injection seam for every
// routine fire) and the prompt timing-language lint shared by the HTTP route
// and the structured create lane.
export { composeRoutineFireMessage, renderTriggerPayload } from '../routines/fire-framing.js';
export type { RoutineFireMessageArgs } from '../routines/fire-framing.js';
export { lintRoutinePrompt, ROUTINE_PROMPT_LINT_PATTERNS } from '../routines/prompt-lint.js';
export type { RoutinePromptLintPattern, RoutinePromptLintResult } from '../routines/prompt-lint.js';

// =============================================================================
// Autopilot
// =============================================================================

export type * from './autopilot.js';

// =============================================================================
// Subscription Plans
// =============================================================================

export type { SubscriptionPlan } from './subscription.js';
export {
  SUBSCRIPTION_PLANS,
  PLAN_LEVELS,
  PLAN_LABELS,
  PLAN_SANDBOX_MEMORY_MB,
  PLAN_SANDBOX_MEMORY_MB_SMALL_START,
  VOLUME_INITIAL_MB,
  PLAN_VOLUME_CEILING_MB,
  hasMinimumPlan,
} from './subscription.js';
export * from '../billing/prices.js';

// =============================================================================
// Onboarding
// =============================================================================

export type {
  OnboardingStatus,
  UseCase,
  OnboardingData,
  ArrivalRoutineDef,
  OnboardingCompleteRequest,
  OnboardingStatusResponse,
} from './onboarding.js';
export * from './attribution.js';

// =============================================================================
// User capabilities (carried by USER_CAPABILITIES socket event)
// =============================================================================

export type { UserCapabilities } from './capabilities.js';

// =============================================================================
// Browser streaming (Steel + Stagehand — 2026-04-11)
// =============================================================================

export type {
  IceServer,
  BrowserStreamReadyPayload,
  BrowserStreamStoppedPayload,
  BrowserStreamSuspendedPayload,
  BrowserStreamResumedPayload,
  BrowserTab,
  BrowserTabsUpdatedPayload,
  BrowserTabsPayload,
  BrowserNavigateAction,
  BrowserNavigatePayload,
  BrowserOpenSessionPayload,
  BrowserPaneVisibilityPayload,
  ComputerSoundPayload,
  ComputerSoundAck,
  BrowserOpenTabPayload,
  BrowserCloseTabPayload,
  BrowserLocalReadyPayload,
  BrowserEmbeddedReadyPayload,
  BrowserExtensionReadyPayload,
  BrowserLocalReadyEventPayload,
  BrowserLocalStoppedPayload,
  BrowserMirrorEventPayload,
  BrowserMirrorResnapshotPayload,
} from './browser-stream.js';

// =============================================================================
// Browser stats (Phase 1 streaming observability — 2026-04-29)
// =============================================================================

export type { BrowserStatsPayload } from './browser-stats.js';

// =============================================================================
// Browser view (BROWSER_VIEW_MACHINE_V1 — server-owned view snapshot)
// =============================================================================

export * from './browser-view.js';

// =============================================================================
// Live Localhost Preview (APP_PREVIEW_V1 — durable pointer + wake reply)
// =============================================================================

export * from './live-preview.js';

// =============================================================================
// Terminal tab (V1 line-based exec — 2026-05-26)
// =============================================================================

export type {
  TerminalExecPayload,
  TerminalResultPayload,
} from './terminal.js';

// =============================================================================
// UI/Client Types (small, no dedicated file needed)
// =============================================================================

/**
 * Agent processing state for UI
 */
export interface AgentState {
  isProcessing: boolean;
  currentToolUse?: string;
  /** List of agents currently processing */
  processingAgents: string[];
}

/**
 * Socket connection status
 */
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * Token usage from Claude API response
 */
export interface TokenUsage {
  inputTokens?: number;
  outputTokens?: number;
  cacheReadInputTokens?: number;
  cacheCreationInputTokens?: number;
}

// =============================================================================
// Interaction — unified user-input primitive
// See docs/superpowers/specs/2026-04-20-input-station.md
// =============================================================================

export type {
  InteractionKind,
  PermissionSuggestion,
  InteractionRequestBase,
  AskQuestionRequest,
  ComposioAuthRequest,
  ToolPermissionRequest,
  ToolPermissionDetail,
  ToolPermissionReason,
  CreditApprovalRequest,
  ExitPlanRequest,
  CustomInteractionRequest,
  HandsApprovalRequest,
  PlanLimitOtherPlan,
  PlanLimitRequest,
  PlanLimitChoice,
  EmailSendApprovalRequest,
  InteractionRequest,
  InteractionDenyReason,
  InteractionAllow,
  InteractionDeny,
  InteractionOutcome,
  InteractionResponse,
  InteractionRequiredPayload,
  InteractionResponsePayload,
  ContinueInteractionPayload,
  TriggeredBy,
  SubagentPathSegment,
  SubagentId,
} from './interaction.js';

// =============================================================================
// Jobs Framework (FAL Backend Integration)
// =============================================================================

export type {
  NoDoorFlag,
  UniversalResult,
  UniversalResultKind,
} from './job-result.js';

// =============================================================================
// Channel Gateway (Slice 1 — Telegram ingress)
// =============================================================================

export { CHANNEL_TYPES, isChannelType, FETCHABLE_MEDIA, isFetchableMedia, REACTION_KEYS, isReactionKey } from './channel.js';
export type {
  ChannelType,
  InboundMessage,
  InboundGroup,
  ChannelLink,
  ChannelConversation,
  InboundMediaRef,
  MediaKind,
  VisionImage,
  ReactionKey,
} from './channel.js';

// =============================================================================
// Nami Observations Pool (NAMI_KEEPER PR1 — closed source/epistemic unions)
// =============================================================================
export {
  NAMI_SOURCES,
  NAMI_EPISTEMICS,
  NAMI_AUTHORS,
  isNamiSource,
  isNamiEpistemic,
  isNamiAuthor,
  groundEpistemic,
  isUserAuthored,
} from './nami.js';
export type { NamiSource, NamiEpistemic, NamiAuthor, Observation } from './nami.js';

// =============================================================================
// Nami Proposals
// =============================================================================
export {
  isSkillProposal,
  isTaskProposal,
  isRoutineProposal,
  isPrepProposal,
  PREP_PROPOSAL_GRACE_MS,
} from './nami-proposals.js';
export type {
  NamiProposal,
  NamiProposalKind,
  NamiProposalStatus,
  NamiSkillPayload,
  NamiTaskPayload,
  NamiRoutinePayload,
  NamiPermissionRulePayload,
  NamiPrepProposalPayload,
} from './nami-proposals.js';

// =============================================================================
// Nami Brief (P5-M2 daily brief)
// =============================================================================
export type {
  NamiBrief,
  NamiBriefRefs,
  NamiBriefItemRef,
  NamiBriefItemKind,
  NamiBriefSettings,
} from './nami-brief.js';

// =============================================================================
// Nami Digest (P5-M3 weekly digest — the trust ritual)
// =============================================================================
export type {
  NamiDigest,
  NamiDigestItems,
  NamiDigestCard,
  NamiDigestCardKind,
  NamiDigestVerdict,
  NamiDigestVerdictBody,
  NamiDigestVerdictLogEntry,
} from './nami-digest.js';

// =============================================================================
// Nami Prep Pack (P6-M1 meeting prep pack — rung A2, first unsolicited surface)
// =============================================================================
export type {
  NamiPrepPack,
  NamiPrepAnchorRef,
  NamiPrepPackRefs,
  NamiPrepPackItemRef,
  NamiPrepPackItemKind,
  NamiPrepPackResolveBody,
  NamiPrepPackSource,
  NamiPrepPackView,
} from './nami-prep-pack.js';

// =============================================================================
// Nami aha dashboard (Nami Delight Train 0)
// =============================================================================
export type { NamiAhaWeek, NamiAhaUserWeek, NamiAhaUser, NamiAhaReport } from './nami-aha.js';

// =============================================================================
// Nami Routine Run Report (P8-M2 — rung A4, the accountability loop)
// =============================================================================
export type {
  NamiRoutineReport,
  NamiRoutineReportPayload,
  NamiRoutineRunOutcome,
} from './nami-routine-report.js';

export type { StoredInboxPayload, InlineImageId } from './task-inbox.js';
export type {
  TaskOutputDoc,
  OutputBlock,
  OutputDocKind,
  OutputHeadingBlock,
  OutputRowBlock,
  OutputKvBlock,
  OutputStatBlock,
  OutputBulletBlock,
  OutputQuoteBlock,
  OutputParagraphBlock,
} from './task-output-doc.js';
export { parseOutputDoc, OUTPUT_DOC_MAX_BLOCKS, OUTPUT_DOC_MAX_BYTES } from './task-output-doc.js';
export { TASK_INBOX_CAPABILITY } from './task-inbox.js';

export type {
  RunnerLeasePayload,
  LeaseAcquireRequest,
  LeaseAcquireResponse,
  LeaseHeartbeatRequest,
  LeaseHeartbeatResponse,
  BoxRecipeReport,
  LeaseReleaseReason,
  LeaseReleaseRequest,
  LeaseReleaseResponse,
  RunnerDrainPayload,
} from './runner-lease.js';
export { RUNNER_LEASE_CAPABILITY, WAKE_RELAUNCH_CAPABILITY, DEFAULT_LEASE_ENVIRONMENT, LOOP_STALL_LOG_MS } from './runner-lease.js';
export { OFFLOOP_CHARGE_HOLD_CAPABILITY, RUNNER_DEVICE_TOKEN_HEADER, DEVICE_TOKEN_RENEW_BEFORE_MS } from './runner-lease.js';

export type {
  WorkspaceHeadRecord,
  WorkspaceHeadUploadUrlRequest,
  WorkspaceHeadUploadUrlResponse,
  WorkspaceHeadRecordRequest,
  WorkspaceHeadRecordResponse,
  WorkspaceHeadLatestResponse,
  WorkspaceHeadMismatchEvent,
  WorkspaceMaterializedEvent,
  MaterializeTimings,
} from './workspace-head.js';
export {
  WORKSPACE_HANDOFF_BUCKET,
  WORKSPACE_HEAD_MISMATCH_EVENT,
  WORKSPACE_HEAD_CAPABILITY,
  WORKSPACE_HEAD_MAX_BYTES,
} from './workspace-head.js';
export type { WorkspaceHeadFormat, WorkspaceManifestEntry, WorkspaceManifestDoc, WorkspaceBlobsMissingRequest, WorkspaceBlobsMissingResponse, WorkspaceBlobUrlsRequest, WorkspaceBlobUrlsResponse, WorkspaceBlobIdsRequest, WorkspaceBlobForgetRequest } from './workspace-head.js';
export { WORKSPACE_BLOB_SOLO_MIN_BYTES, WORKSPACE_BLOB_PIECE_BYTES, WORKSPACE_GROUP_MAX_FILES, WORKSPACE_BLOBS_PAGE, WORKSPACE_BLOB_URLS_BATCH, WORKSPACE_RECORD_BUDGET_MS, blobIdsOf, objectIdsOf } from './workspace-head.js';

export type {
  RelayResumePayload,
  RelayResumedPayload,
  RelayAckPayload,
  RelayGapPayload,
  SequencedEnvelope,
} from './relay-frames.js';
export { TURN_REATTACH_CAPABILITY, RELAY_SUPERSEDED_CLOSE_CODE, SEQ_EXEMPT_EVENTS, sequencedTaskId } from './relay-frames.js';

// =============================================================================
// Session Search + Spawn (session_search / session_create tools — rung 1)
// =============================================================================

export type { SessionSearchHit, SessionSearchWindowMessage, SessionSummaryBody, SessionSearchDiscoverResponse, SessionSearchScrollResponse, SessionSearchReadResponse, SessionSearchBrowseResponse, SessionSpawnRequest, SessionSpawnResponse } from './session-search.js';

// =============================================================================
// GTM Lead Engine (internal admin CRM — apps/dashboard /admin/gtm)
// =============================================================================

export type {
  GtmLeadStage,
  GtmTier,
  GtmChannel,
  GtmMessageStatus,
  GtmSendAdapter,
  GtmImportStatus,
  GtmActivityType,
  GtmLead,
  GtmLeadImport,
  GtmMessage,
  GtmActivity,
  GtmConnectorKind,
  GtmConnectorInfo,
  GtmConnectorsStatus,
  GtmOperatorUsage,
  GtmLeadListResponse,
  GtmLeadDetailResponse,
  GtmBulkDraftPreview,
  GtmBulkDraftJob,
  GtmSignalType,
  GtmSignalPlatform,
  GtmSignal,
  GtmIcp,
  GtmBeachheadStatus,
  GtmSubSegment,
  GtmSubSegmentDef,
  GtmIcpPreset,
  GtmIcpPresetResult,
  GtmIcpMixRow,
  GtmDesignPartnerStatus,
  GtmDesignPartner,
  GtmPmfSnapshot,
  GtmPmfMetric,
  GtmPmfGate,
  GtmPmfBoard,
  GtmEnrollmentStatus,
  GtmSequenceStep,
  GtmSequence,
  GtmEnrollment,
  GtmEnrollmentView,
  GtmQueueItem,
  GtmSequenceStats,
  GtmLaunchStatus,
  GtmLaunch,
  GtmDirectoryStatus,
  GtmDirectory,
  GtmDiscoveryCategory,
  GtmDiscoveryPricingModel,
  GtmDirectoryRequirements,
  GtmDiscoveryPlatform,
  GtmListingProfile,
  GtmWeeklyItem,
  GtmWeeklyState,
  GtmOpsMetrics,
  GtmContentPlatform,
  GtmInfluencerStatus,
  GtmInfluencerAnalysis,
  GtmInfluencer,
  GtmSnipeStatus,
  GtmSnipeVerdict,
  GtmSnipeAnalysis,
  GtmContentSnipe,
  GtmLeadMagnet,
  GtmSendPacing,
  GtmSection,
  GtmBulkEnrichResult,
  GtmWaterfallStartResult,
  GtmWaterfallPollResult,
  GtmApolloUsage,
} from './gtm.js';
export { GTM_WEEKLY_LOOP } from './gtm.js';
export { GTM_SUB_SEGMENTS, icpForSubSegment, beachheadStatusForSubSegment } from './gtm.js';
export { GTM_SECTIONS, GTM_ICP_PROSPECT_PRESETS } from './gtm.js';

// Work Health (internal admin accountability — apps/dashboard /admin/work-health)
export {
  WORK_HEALTH_PLATFORMS,
  WORK_HEALTH_PLATFORM_META,
  WORK_HEALTH_WINDOWS,
  WORK_HEALTH_WINDOW_DAYS,
  WORK_HEALTH_SOURCE_TYPES,
} from './work-health.js';
export type {
  WorkHealthPlatform,
  WorkHealthWindow,
  WorkHealthStatus,
  WorkHealthUsageSource,
  WorkHealthSourceType,
  WorkHealthMember,
  WorkHealthResponsibility,
  WorkHealthUsage,
  WorkHealthResponsibilityReport,
  WorkHealthMemberReport,
  WorkHealthReport,
} from './work-health.js';

// =============================================================================
// Needs-you kind taxonomy (home shell — closed set of nine kinds)
// =============================================================================

export {
  NEED_KINDS,
  NEED_KIND_LABELS,
  NEED_KIND_CSS_VAR,
  needKindForInteraction,
} from './need-kind.js';
export type { NeedKind } from './need-kind.js';

export {
  deriveAutopilotNeedCard,
  deriveNeedCard,
  deriveProposalNeedCard,
  excerpt,
  formatUsd,
  permissionHeadline,
} from './need-card.js';
export type {
  NeedCardAction,
  NeedCardContext,
  NeedCardIntent,
  NeedCardModel,
} from './need-card.js';

// =============================================================================
// Board Stream (home-streams lane — slim home-board realtime delta)
// =============================================================================

export { BoardTaskDeltaSchema } from './board-stream.js';
export type { BoardTaskDelta } from './board-stream.js';
export { STORY_PHASES, STORY_KINDS, StoryEntrySchema, StoryEntryDeltaSchema } from './story.js';
export type { StoryPhase, StoryKind, StoryEntry, StoryEntryDelta } from './story.js';

// =============================================================================
// Global search (the search_everything RPC wire contract)
// =============================================================================

export { SEARCH_TYPES } from './global-search.js';
export type {
  SearchType,
  SearchHit,
  SearchGroup,
  GlobalSearchResponse,
  GlobalSearchParams,
} from './global-search.js';

// =============================================================================
// Account block (admin block / unblock, the suspended screen)
// =============================================================================

export {
  ACCOUNT_BLOCK_REASONS,
  ACCOUNT_BLOCK_REASON_LABELS,
  ACCOUNT_SUPPORT_EMAIL,
  accountReference,
  referenceToIdPrefix,
  blockNoticeText,
} from './account-block.js';
export type {
  AccountBlockReason,
  AccountBlockErrorCode,
  AccountBlockStatus,
  AdminUserBlockEvent,
  AdminUserBlockView,
} from './account-block.js';

export {
  BOAT_MACHINE_SIZES,
  BOAT_MACHINE_TYPES,
  boatMachineSizeLabel,
  CLOUD_HOST_LABELS,
  CLOUD_HOST_SWITCH_ERRORS,
  CLOUD_HOST_SWITCH_REASON_MAX,
  CLOUD_HOST_SWITCH_REFUSALS,
  START_DAY_SHARE_PAGE,
} from './cloud-host.js';
export type {
  AdminCloudHostEvent,
  AdminHostDevice,
  AdminHostMachine,
  AdminMachineState,
  AdminStartLimits,
  AdminUserHostView,
  BoatMachineType,
  CloudHostSwitchError,
  CloudHostSwitchResult,
  StartRefusalKind,
  StartWindow,
} from './cloud-host.js';
